<template>
    <Multiselect
        :ref="el => {multiselectReference = el}"
        mode="multiple"
        :placeholder="placeholder"
        v-model="selected"
        :filterResults="false"
        :closeOnSelect="false"
        :hideSelected="false"
        :clearOnSelect="false"
        :clearOnSearch="true"
        :canClear="false"
        :delay="-1"
        :minChars="1"
        :searchable="true"
        :disabled="initialLoading"
        :options="options"
        :classes="classes"
        :loading="loading"
        :multipleLabel="multipleSelectLabel"
        @searchChange="handleOnSearchChange"
        @select="handleOnSelect"
        @deselect="handleOnDeselect"
        :allowAbsent="true"
    >
        <template #beforelist>
            <div
                v-if="showSelectAll"
                class="flex flex-row justify-between pl-4 pr-2 py-2"
            >
                <Checkbox :modelValue="allOptionsAreIncluded"
                          @update:modelValue="toggleSelectAll($event)"
                          :label="`Select all (${totalOptions})`"
                          :class="[allOptionsAreIncluded && 'hover:opacity-50','focus:ring-0 focus:ring-transparent h-4 border-interactive-6 rounded text-primary-1 mt-2 w-full hover:text-primary-1']">
                </Checkbox>
                <span class="text-body-6 text-default-4 font-normal cursor-pointer hover:text-primary-1 w-full text-right"
                      @click="toggleSelectAll(false)">Clear all</span>
            </div>
        </template>
        <template #afterlist>
          <InfiniteScrollObserver
              v-if="!loading && hasMore"
              @intersect="handleScroll"
          >
            <div class="h-8"/>
          </InfiniteScrollObserver>
            <div v-if="loading" class="flex flex-row justify-between pl-4 pr-2 py-2">
                <span class="select-none relative capitalize text-primary-1 cursor-text">
                    Loading more...
                 </span>
            </div>
        </template>

        <template #option="{ option, isSelected }">
            <Checkbox :model-value="isSelected(option) || allOptionsAreIncluded" @click.prevent></Checkbox>
            <span class="select-none relative -mb-px capitalize pl-2.5">
                {{ option.label }}
            </span>
        </template>
    </Multiselect>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, toRef} from "vue";
import Multiselect from "@vueform/multiselect";
import Checkbox from "@/components/core/Checkbox.vue";
import {useAuth} from "@/stores/AuthStore";
import {cloneDeep} from "lodash";
import {useIncludeExcludeOptions} from "@/composables/useIncludeExcludeOptions";
import InfiniteScrollObserver from "../InfiniteScrollObserver.vue";
import { v4 as uuidv4 } from 'uuid'

const props = defineProps({
    resourceName: {
        type: String,
        required: true
    },
    resourceCallback: {
        type: Function,
        required: true
    },
    resourceLabel: {
        type: [String, Function],
        required: true,
    },
    resourceIdentifier: {
        type: String,
        required: true,
        default: 'uid'
    },
    hasError: {
        type: Boolean,
        default: false
    },
    defaultOptions: {
        type: Array,
        default: () => []
    },
    showSelectAll: {
        type: Boolean,
        default: true
    },
    openOnMount: {
        type: Boolean,
        default: false
    },
    hasOrganization: {
        type: Boolean,
        default: true
    },
    perPage: {
        type: Number,
        default: 10
    }
});

const emit = defineEmits(['select']);

const multiselectReference = ref(null);
const hasError = toRef(props, 'hasError');
const {organization} = useAuth();
const currentPage = ref(1);
const hasMore = ref(true);
const totalOptions = ref(null);
const selected = ref([]);
const options = ref([]);
const loading = ref(false);
const initialLoading = ref(false);

const {
    include,
    exclude,
    isIncluded,
    includeAll,
    excludeAll,
    options: includedOptions,
    allOptionsAreIncluded,
} = useIncludeExcludeOptions(totalOptions, props.resourceName + uuidv4());

const placeholder = computed(() => {
    const resourceName = props.resourceName.slice(0, -1);

    return `Enter or search for a ${resourceName}`;
});

onMounted(async () => {
    initialLoading.value = true;

    if(multiselectReference.value) {
        multiselectReference.value.clearSearch();
    }

    selected.value = props.defaultOptions;
    include(props.defaultOptions);

    initialLoading.value = false;

    if(props.openOnMount) {
        await nextTick(() => {
            multiselectReference.value.open()
            multiselectReference.value.focus()
        })
    }
});

function toggleSelectAll(selectAll) {
    if (selectAll) {
        multiselectReference.value.selectAll();
        includeAll();
    } else {
        multiselectReference.value.clear();
        excludeAll();
    }

    emit('select', cloneDeep(includedOptions.value));
}

function multipleSelectLabel() {
    const { include, exclude } = includedOptions.value;

    if (allOptionsAreIncluded.value || include.includes('*')) {
        return `${totalOptions.value - exclude.length} ${props.resourceName} selected`;
    }

    if(include.length === 1){
        return `${include.length} ${props.resourceName.slice(0, -1)} selected`;
    }

    return `${include.length} ${props.resourceName} selected`;
}

function handleOnDeselect(option) {
    exclude(option);

    emit('select', cloneDeep(includedOptions.value));
}

function handleOnSelect(option) {
    if (option === undefined) {
        return;
    }

    include(option);

    emit('select', cloneDeep(includedOptions.value))
}

async function handleOnSearchChange(searchBy) {
    currentPage.value = 1;
    hasMore.value = false;
    options.value = await searchForOptions(searchBy);
}

async function handleScroll() {
    if (!hasMore.value || loading.value) {
        return;
    }

    currentPage.value += 1;

    let values = await searchForOptions(multiselectReference.value.search, currentPage.value, props.perPage);

    options.value = [...options.value, ...values];

    options.value.forEach(option => {
        if (isIncluded(option.value)) {
            multiselectReference.value.select(option);
        }
    });
}

async function searchForOptions(searchBy = null, page = 1) {
    loading.value = true;
    searchBy = searchBy ? searchBy.trim() : null;

    let options = [];

    if (props.hasOrganization) {
        options = await props.resourceCallback(
            organization.slug,
            page,
            props.perPage,
            searchBy ?? ''
        );
    } else {
        options = await props.resourceCallback(
            page,
            props.perPage,
            searchBy ?? ''
        );
    }

    loading.value = false;

    if (totalOptions.value === null) {
        totalOptions.value = options.meta.total;
    }

    hasMore.value = options.meta.current_page < options.meta.last_page;

    return options.data.map(option => {
        return {
            label: typeof props.resourceLabel === 'string'  ? `${option[props.resourceLabel]}` : props.resourceLabel(option),
            value: option[props.resourceIdentifier],
        };
    });
}

const classes = ref({
    container: {
        'multiselect rounded text-body-6': true,
        'border-primary-2': hasError,
        'focus:outline-none focus:ring-transparent focus:border-primary-1 hover:border-primary-1 hover:border': true,
    },
    containerOpen: 'border-primary-1',
    search: 'w-full absolute inset-0 outline-none focus:ring-0 appearance-none box-border border-0 text-default-1 text-body-6 font-sans rounded pl-3.5 rtl:pl-0 rtl:pr-3.5',
    option: 'flex items-center justify-start box-border text-left cursor-pointer leading-snug pl-4 pr-2 py-2 text-body-6 text-default-1',
    optionPointed: 'text-primary-1 cursor-pointer bg-background-3',
    optionSelected: 'bg-background-3',
    optionDisabled: 'text-gray-300 cursor-not-allowed',
    optionSelectedPointed: 'bg-background-3',
    optionSelectedDisabled: 'text-green-100 bg-green-500 bg-opacity-50 cursor-not-allowed',
    placeholder: 'multiselect-placeholder whitespace-nowrap',
    dropdown: 'xxs:max-h-[360px] lg:max-h-[480px] absolute z-[10] -left-px -right-px bottom-0 transform translate-y-full border border-gray-300 -mt-px overflow-y-scroll bg-white flex flex-col rounded-b',
    dropdownTop: '-translate-y-full top-px bottom-auto rounded-b-none rounded-t',
    dropdownHidden: 'hidden',
});
</script>
